/**
 * voiceMetadata.js
 * ─────────────────
 * Utilitários puros pra trabalhar com o objeto `voice.voice_info` vindo da API
 * do Carlos (audio.darkplanner.com.br). Conversões pt-BR, mapeamento de
 * bandeiras (ISO 3166), helpers de idiomas verificados, e facetCounts pra
 * filtros estilo Algolia (multi-select, contagem dinâmica).
 *
 * IMPORTANTE: ZERO hooks aqui. Componentes que precisarem cache (useMemo)
 * fazem isso por conta própria.
 */

// ═══════════════════════════════════════════════════════════════════════════
// FLAG MAP — language[+accent] → código de país ISO 3166 (alpha-2 minúsculo)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Exceções: combinação language+accent específica → bandeira diferente do default.
 * Chave: "language|accent" (ambos lower-case)
 */
const LANGUAGE_ACCENT_MAP = {
  // Inglês — sotaques regionais
  'en|british': 'gb',
  'en|australian': 'au',
  'en|irish': 'ie',
  'en|scottish': 'gb',
  'en|south_african': 'za',
  'en|south african': 'za',
  'en|canadian': 'ca',
  'en|indian': 'in',
  // Português — peninsular / europeu
  'pt|peninsular': 'pt',
  'pt|european': 'pt',
  'pt|portugal': 'pt',
  // Espanhol — variantes regionais
  'es|mexican': 'mx',
  'es|argentinian': 'ar',
  'es|argentine': 'ar',
  'es|chilean': 'cl',
  'es|colombian': 'co',
  'es|peruvian': 'pe',
  'es|venezuelan': 'co',
  'es|cuban': 'cu',
  'es|latin_american': 'mx',
  'es|latin american': 'mx',
  'es|castilian': 'es',
};

/**
 * Default por idioma (quando o accent é "standard"/"neutral"/ausente,
 * ou quando não há exceção pro pair language+accent).
 */
const LANGUAGE_DEFAULT_MAP = {
  en: 'us',
  pt: 'br',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
  ja: 'jp',
  zh: 'cn',
  ko: 'kr',
  hi: 'in',
  ta: 'in',
  bn: 'bd',
  ur: 'pk',
  nl: 'nl',
  pl: 'pl',
  tr: 'tr',
  ru: 'ru',
  ar: 'sa',
  sv: 'se',
  id: 'id',
  fil: 'ph',
  tl: 'ph',
  vi: 'vn',
  uk: 'ua',
  el: 'gr',
  cs: 'cz',
  da: 'dk',
  fi: 'fi',
  no: 'no',
  nb: 'no',
  he: 'il',
  ms: 'my',
  ro: 'ro',
  sk: 'sk',
  hu: 'hu',
  hr: 'hr',
  bg: 'bg',
  sr: 'rs',
  sl: 'si',
  lt: 'lt',
  lv: 'lv',
  et: 'ee',
  ca: 'es',
  th: 'th',
  af: 'za',
  sw: 'ke',
};

/**
 * Aliases de família de códigos linguísticos: prefix do locale → language code.
 * Permite aceitar `cmn-CN` como locale de uma voz com language="zh", etc.
 */
const FAMILY_ALIASES = {
  cmn: 'zh',  // mandarim
  jv: 'id',   // javanês (Indonésia)
  ceb: 'fil', // cebuano (Filipinas)
  nb: 'no',   // norueguês bokmål
};

/**
 * Retorna o código de país ISO 3166 alpha-2 (ex.: "us", "br") pra usar em
 * `flag-icons` como `fi fi-{code}`. Retorna "xx" quando não é possível mapear.
 *
 * Estratégia (ITER4.1):
 *   1. Se `locale` informado (ex.: "pt-PT", "sk-SK", "cmn-TW") E o prefix do
 *      locale CASA com `language` (ou com um alias de família — cmn↔zh,
 *      jv↔id, ceb↔fil, nb↔no): usa o sufixo do locale como bandeira.
 *      Isso resolve pt-PT vs pt-BR, sk-SK, en-NG, nl-BE, fr-CA, etc.
 *   2. Se houver conflito entre locale.prefix e language (ex.: language="ru"
 *      mas locale="ro-RO" — dado errado no upstream): IGNORA locale e cai pro
 *      mapeamento por language+accent.
 *   3. Fallback final: LANGUAGE_ACCENT_MAP → LANGUAGE_DEFAULT_MAP → "xx".
 *
 * @param {string|null|undefined} language
 * @param {string|null|undefined} accent
 * @param {string|null|undefined} locale  ISO BCP 47 (ex.: "pt-BR")
 * @returns {string}
 */
export function getFlagCode(language, accent, locale = null) {
  if (!language || typeof language !== 'string') return 'xx';
  const lang = language.trim().toLowerCase();
  if (!lang) return 'xx';

  // 1. Tentar locale primeiro, com verificação de consistência
  if (locale && typeof locale === 'string' && locale.includes('-')) {
    const parts = locale.trim().toLowerCase().split('-');
    const localePrefix = parts[0];
    const localeSuffix = parts[1];
    if (localeSuffix && localeSuffix.length >= 2) {
      const localeAlias = FAMILY_ALIASES[localePrefix] || localePrefix;
      // Aceita se prefix do locale == language OU se for alias de família
      if (localeAlias === lang || localePrefix === lang) {
        return localeSuffix;
      }
      // Caso especial: pt sem distinguir BR/PT no prefix — sempre aceita pt-XX
      if (lang === 'pt' && localePrefix === 'pt') {
        return localeSuffix;
      }
      // Senão: conflito (ex.: ru + ro-RO) — ignora locale e cai pro accent map
    }
  }

  // 2. Mapeamento por language+accent
  if (accent && typeof accent === 'string') {
    const acc = accent.trim().toLowerCase();
    const key = `${lang}|${acc}`;
    if (LANGUAGE_ACCENT_MAP[key]) return LANGUAGE_ACCENT_MAP[key];
  }
  // 3. Default por language
  return LANGUAGE_DEFAULT_MAP[lang] || 'xx';
}

/**
 * Retorna o código de bandeira "primário" de uma voz, tentando enriquecer com
 * o `locale` da entry de `verified_languages` que corresponde ao primário
 * (mesmo language+accent). Se a voz não tem voice_info, retorna "xx".
 *
 * Usado pra renderizar a bandeira principal nos cards (sidebar lateral + grid).
 *
 * @param {Object} voice
 * @returns {string}
 */
export function getPrimaryFlagCode(voice) {
  if (!voice || !voice.voice_info) return 'xx';
  const info = voice.voice_info;
  const lang = info.language || null;
  const acc = info.accent || null;
  // Procura locale correspondente em verified_languages
  let locale = null;
  if (Array.isArray(info.verified_languages)) {
    const langLower = (lang || '').toLowerCase();
    const accLower = (acc || '').toLowerCase();
    const match = info.verified_languages.find((e) => {
      if (!e) return false;
      const l = (e.language || '').toLowerCase();
      const a = (e.accent || '').toLowerCase();
      return l === langLower && a === accLower;
    });
    if (match && match.locale) locale = match.locale;
  }
  return getFlagCode(lang, acc, locale);
}

// ═══════════════════════════════════════════════════════════════════════════
// TRADUÇÕES pt-BR
// ═══════════════════════════════════════════════════════════════════════════

export const GENDER_PT = {
  male: 'Masculino',
  female: 'Feminino',
  neutral: 'Neutro',
  non_binary: 'Não-binário',
};

export const AGE_PT = {
  young: 'Jovem',
  middle_aged: 'Adulto',
  old: 'Idoso',
};

export const CATEGORY_PT = {
  professional: 'Profissional',
  high_quality: 'Alta Qualidade',
  premade: 'Padrão',
  famous: 'Famoso',
  cloned: 'Clonada',
  generated: 'Gerada',
};

export const ACCENT_PT = {
  american: 'Americano',
  british: 'Britânico',
  australian: 'Australiano',
  irish: 'Irlandês',
  scottish: 'Escocês',
  canadian: 'Canadense',
  indian: 'Indiano',
  south_african: 'Sul-Africano',
  'south african': 'Sul-Africano',
  brazilian: 'Brasileiro',
  peninsular: 'Europeu',
  european: 'Europeu',
  portugal: 'Português (PT)',
  mexican: 'Mexicano',
  argentinian: 'Argentino',
  argentine: 'Argentino',
  chilean: 'Chileno',
  colombian: 'Colombiano',
  peruvian: 'Peruano',
  cuban: 'Cubano',
  latin_american: 'Latino-Americano',
  'latin american': 'Latino-Americano',
  castilian: 'Castelhano',
  parisian: 'Parisiense',
  italian: 'Italiano',
  german: 'Alemão',
  japanese: 'Japonês',
  korean: 'Coreano',
  chinese: 'Chinês',
  russian: 'Russo',
  arabic: 'Árabe',
  turkish: 'Turco',
  dutch: 'Holandês',
  polish: 'Polonês',
  swedish: 'Sueco',
  danish: 'Dinamarquês',
  finnish: 'Finlandês',
  norwegian: 'Norueguês',
  hindi: 'Hindi',
  standard: 'Padrão',
  neutral: 'Neutro',
};

export const DESCRIPTIVE_PT = {
  calm: 'Calma',
  warm: 'Calorosa',
  confident: 'Confiante',
  deep: 'Profunda',
  gentle: 'Gentil',
  intense: 'Intensa',
  raspy: 'Rouca',
  smooth: 'Suave',
  strong: 'Forte',
  soft: 'Macia',
  energetic: 'Energética',
  mature: 'Madura',
  friendly: 'Amigável',
  mysterious: 'Misteriosa',
  sad: 'Triste',
  happy: 'Alegre',
  neutral: 'Neutra',
  expressive: 'Expressiva',
  articulate: 'Articulada',
  animated: 'Animada',
  professional: 'Profissional',
  casual: 'Casual',
  serious: 'Séria',
  authoritative: 'Autoritária',
  wise: 'Sábia',
  cheerful: 'Animada',
  upbeat: 'Otimista',
  excited: 'Empolgada',
  relaxed: 'Relaxada',
  emotional: 'Emocional',
  dramatic: 'Dramática',
  pleasant: 'Agradável',
};

export const USE_CASE_PT = {
  narration: 'Narração',
  narrative_story: 'Narrativa / Storytelling',
  news: 'Notícias',
  conversational: 'Conversacional',
  informative_educational: 'Informativo / Educacional',
  advertisement: 'Publicidade',
  social_media: 'Mídias Sociais',
  audiobook: 'Audiolivro',
  characters: 'Personagens',
  characters_animation: 'Animação',
  animation: 'Animação',
  video_games: 'Videogames',
  'e-learning': 'E-learning',
  e_learning: 'E-learning',
  podcast: 'Podcast',
  meditation: 'Meditação',
  asmr: 'ASMR',
  entertainment_tv: 'Entretenimento',
  entertainment: 'Entretenimento',
  ivr: 'IVR / Atendimento',
};

export const PROVIDER_PT = {
  '11labs': '11Labs',
  'capcut': 'CapCut',
};

export const LANGUAGE_PT = {
  en: 'Inglês',
  pt: 'Português',
  es: 'Espanhol',
  fr: 'Francês',
  de: 'Alemão',
  it: 'Italiano',
  ja: 'Japonês',
  zh: 'Chinês',
  ko: 'Coreano',
  hi: 'Hindi',
  ta: 'Tâmil',
  bn: 'Bengali',
  ur: 'Urdu',
  nl: 'Holandês',
  pl: 'Polonês',
  tr: 'Turco',
  ru: 'Russo',
  ar: 'Árabe',
  sv: 'Sueco',
  id: 'Indonésio',
  fil: 'Filipino',
  tl: 'Filipino',
  vi: 'Vietnamita',
  uk: 'Ucraniano',
  el: 'Grego',
  cs: 'Tcheco',
  da: 'Dinamarquês',
  fi: 'Finlandês',
  no: 'Norueguês',
  nb: 'Norueguês',
  he: 'Hebraico',
  ms: 'Malaio',
  ro: 'Romeno',
  sk: 'Eslovaco',
  hu: 'Húngaro',
  hr: 'Croata',
  bg: 'Búlgaro',
  sr: 'Sérvio',
  sl: 'Esloveno',
  lt: 'Lituano',
  lv: 'Letão',
  et: 'Estoniano',
  ca: 'Catalão',
  th: 'Tailandês',
  af: 'Afrikaans',
  sw: 'Suaíli',
};

/**
 * Capitaliza um valor "snake_case" pra exibir como fallback.
 * Ex: "narrative_story" → "Narrative Story"
 */
function capitalize(value) {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Traduz um valor usando um dict pt-BR. Se não houver tradução,
 * devolve o valor capitalizado.
 *
 * @param {string|null|undefined} value
 * @param {Record<string, string>} dict
 * @returns {string}
 */
export function translate(value, dict) {
  if (!value || typeof value !== 'string') return '';
  const v = value.trim().toLowerCase();
  if (!v) return '';
  if (dict && dict[v]) return dict[v];
  return capitalize(value);
}

// ═══════════════════════════════════════════════════════════════════════════
// VERIFIED LANGUAGES — helpers
// ═══════════════════════════════════════════════════════════════════════════

const PREFERRED_MODEL_ID = 'eleven_multilingual_v2';

/**
 * Dedup entries por language+accent. Pra cada combinação única, prefere a
 * entrada com `model_id == eleven_multilingual_v2`. Se não existir, usa a
 * primeira disponível.
 *
 * Retorna array de objetos com mesmas chaves do upstream (language, accent,
 * locale, model_id, preview_url).
 *
 * @param {Array} verifiedLanguages
 * @returns {Array}
 */
/**
 * Ordem canônica de preferência de accent quando duas entries casam pelo mesmo
 * `language+locale` (ITER4-fix do bug "duas bandeiras BR"). Quanto menor o
 * índice, mais "primário" é o accent — assim "brazilian" ganha de "southern"
 * num empate em pt-BR.
 */
const ACCENT_PRIORITY = [
  'brazilian',
  'european',
  'standard',
  'central',
  'southern',
  'northern',
  'african',
  'madeiran',
  '',
];

function accentRank(accent) {
  const a = (accent || '').toLowerCase();
  const idx = ACCENT_PRIORITY.indexOf(a);
  return idx === -1 ? ACCENT_PRIORITY.length : idx;
}

/**
 * Decide se a entry `candidate` é preferível sobre `existing`. Critérios em
 * ordem (primeiro que distinguir decide):
 *   1) model_id === eleven_multilingual_v2 (modelo padrão de geração interna)
 *   2) accent canônico (ver ACCENT_PRIORITY)
 *   3) primeira ocorrência (retorna false = mantém existing)
 */
function isPreferred(existing, candidate) {
  const exPref = existing.model_id === PREFERRED_MODEL_ID;
  const caPref = candidate.model_id === PREFERRED_MODEL_ID;
  if (exPref !== caPref) return caPref;
  const exRank = accentRank(existing.accent);
  const caRank = accentRank(candidate.accent);
  if (caRank < exRank) return true;
  return false;
}

/**
 * Dedup entries por `language+locale` (mudança ITER4 do bug duplas bandeiras
 * BR — antes era `language+accent` e gerava 2 itens "Português · pt-BR"
 * quando a voz tinha entries `brazilian` E `southern`/`standard` no mesmo
 * locale).
 *
 * Pra cada combinação única (language, locale), mantém UMA entry, escolhendo
 * pela ordem:
 *   1. preferir `model_id == eleven_multilingual_v2`
 *   2. preferir accent canônico (`brazilian` > `european` > `standard` > ...)
 *   3. primeira ocorrência
 *
 * Retorna array de objetos no mesmo formato do upstream (language, accent,
 * locale, model_id, preview_url).
 *
 * @param {Array} verifiedLanguages
 * @returns {Array}
 */
export function uniqueLanguages(verifiedLanguages) {
  if (!Array.isArray(verifiedLanguages)) return [];
  const buckets = new Map();
  for (const entry of verifiedLanguages) {
    if (!entry || typeof entry !== 'object') continue;
    const lang = (entry.language || '').toLowerCase();
    const locale = (entry.locale || '').toLowerCase();
    if (!lang) continue;
    // ITER4-fix: chave agora é language+locale (não mais language+accent)
    const key = `${lang}|${locale}`;
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, entry);
      continue;
    }
    if (isPreferred(existing, entry)) {
      buckets.set(key, entry);
    }
  }
  return Array.from(buckets.values());
}

/**
 * Retorna o melhor preview_url "principal" de uma voz: preferência absoluta
 * pro `voice.voice_info.preview_url` (URL direto ElevenLabs, melhor qualidade),
 * com fallback pro legacy `voice.preview_url` do Carlos (necessário pra
 * vozes sem voice_info, ex.: CapCut e Multivoz).
 *
 * @param {Object|null|undefined} voice
 * @returns {string|null}
 */
export function getPreviewUrl(voice) {
  if (!voice) return null;
  if (voice.voice_info && typeof voice.voice_info === 'object' && voice.voice_info.preview_url) {
    return voice.voice_info.preview_url;
  }
  return voice.preview_url || null;
}

/**
 * O backend agora sanitiza o catálogo (o voice_id ElevenLabs e as URLs
 * diretas da Eleven não saem mais) e entrega os previews como caminhos
 * RELATIVOS do proxy (/api/audio-tts/voice-preview/...). Esta função
 * prefixa esses caminhos com a base do backend logo após o fetch, num
 * ponto único — o resto do front segue consumindo preview_url como antes.
 *
 * @param {Array} voices  lista crua do GET /api/audio-tts/voices
 * @param {string} base   BACKEND_URL (pode ser '' quando mesmo domínio)
 * @returns {Array} nova lista com preview_urls absolutas
 */
export function absolutizeVoicePreviews(voices, base) {
  const prefix = base || '';
  const abs = (u) => (typeof u === 'string' && u.startsWith('/') ? `${prefix}${u}` : u);
  return (voices || []).map((v) => {
    const info = v && v.voice_info;
    if (!info || typeof info !== 'object') return v;
    return {
      ...v,
      voice_info: {
        ...info,
        preview_url: abs(info.preview_url),
        verified_languages: Array.isArray(info.verified_languages)
          ? info.verified_languages.map((e) => (
            e && typeof e === 'object' ? { ...e, preview_url: abs(e.preview_url) } : e
          ))
          : info.verified_languages,
      },
    };
  });
}

/**
 * Acha o melhor preview_url pra (language, accent), preferindo
 * eleven_multilingual_v2. Fallback pra qualquer outro modelo. Retorna
 * null se não encontrar.
 *
 * @param {Array} verifiedLanguages
 * @param {string} language
 * @param {string|null} accent
 * @returns {string|null}
 */
export function getPreviewForLanguage(verifiedLanguages, language, accent = null) {
  if (!Array.isArray(verifiedLanguages) || !language) return null;
  const lang = language.toLowerCase();
  const acc = accent ? accent.toLowerCase() : null;

  const matches = verifiedLanguages.filter((e) => {
    if (!e || typeof e !== 'object') return false;
    if ((e.language || '').toLowerCase() !== lang) return false;
    if (acc) return (e.accent || '').toLowerCase() === acc;
    return true;
  });
  if (!matches.length) return null;

  const preferred = matches.find((e) => e.model_id === PREFERRED_MODEL_ID);
  return (preferred || matches[0]).preview_url || null;
}

/**
 * Retorna o nome do idioma primário da voz em PT (ex.: "Hindi", "Inglês").
 * Usado pela linha de meta da lista lateral + header voz selecionada.
 *
 * @param {Object} voice
 * @returns {string} nome traduzido ou string vazia
 */
export function getPrimaryLanguageName(voice) {
  const lang = voice && voice.voice_info && voice.voice_info.language;
  if (!lang || typeof lang !== 'string') return '';
  return translate(lang, LANGUAGE_PT);
}

/**
 * Resolve nome do país em pt-BR a partir do código ISO 3166 alpha-2
 * (ex.: "in" → "Índia", "gb" → "Reino Unido"). Usa Intl.DisplayNames nativo.
 * Fallback: retorna o código em uppercase.
 *
 * @param {string} flagCode
 * @returns {string}
 */
let _countryDisplayNames = null;
export function getCountryName(flagCode) {
  if (!flagCode || flagCode === 'xx') return 'Idioma';
  const code = String(flagCode).toUpperCase();
  try {
    if (!_countryDisplayNames) {
      _countryDisplayNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });
    }
    return _countryDisplayNames.of(code) || code;
  } catch (_e) {
    return code;
  }
}

/**
 * Retorna o conjunto único de TODOS os idiomas suportados pela voz.
 * Combina `voice_info.language` (primário) + todos os `verified_languages.language`.
 *
 * Vozes sem `voice_info` retornam [].
 *
 * @param {Object} voice
 * @returns {string[]}  códigos ISO em lower-case (ex.: ['en', 'pt', 'es'])
 */
export function getLanguagesOfVoice(voice) {
  const info = voice && voice.voice_info;
  if (!info || typeof info !== 'object') return [];
  const set = new Set();
  if (info.language && typeof info.language === 'string') {
    set.add(info.language.toLowerCase());
  }
  if (Array.isArray(info.verified_languages)) {
    for (const e of info.verified_languages) {
      const l = e && e.language && typeof e.language === 'string' ? e.language.toLowerCase() : null;
      if (l) set.add(l);
    }
  }
  return Array.from(set);
}

// ═══════════════════════════════════════════════════════════════════════════
// FACETED FILTERS — Algolia-style multi-select com contagem dinâmica
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Dimensões suportadas em facetCounts e matchesFilters.
 */
const FACET_DIMENSIONS = ['language', 'accent', 'gender', 'age', 'category', 'use_case', 'provider'];

/**
 * Pega o(s) valor(es) de uma voz pra uma dimensão. Pra 'language' retorna
 * conjunto multi-valor (primário + verified_languages). Pras outras,
 * retorna array com 0 ou 1 valor (singleton).
 *
 * Valores são sempre strings em lower-case ou ['__none__'] pra vozes sem
 * voice_info (CapCut/Multivoz). Componentes consumidores podem optar por
 * mostrar/esconder '__none__' como categoria.
 */
function getValuesForDimension(voice, dimension) {
  if (dimension === 'language') {
    const langs = getLanguagesOfVoice(voice);
    return langs.length ? langs : ['__none__'];
  }
  // GÊNERO: lê SÓ do top-level (Carlos é fonte da verdade). Funciona mesmo
  // quando voice_info está ausente (CapCut, Multivoz, 11Labs não-mapeadas).
  if (dimension === 'gender') {
    const g = (voice && voice.gender) || null;
    return g ? [String(g).toLowerCase()] : ['__none__'];
  }
  // PROVEDOR: também do top-level (Carlos).
  if (dimension === 'provider') {
    const p = (voice && voice.provider) || null;
    return p ? [String(p).toLowerCase()] : ['__none__'];
  }
  // Outras dimensões (accent, age, category, use_case): requerem voice_info
  const info = voice && voice.voice_info;
  if (!info || typeof info !== 'object') return ['__none__'];
  const raw = info[dimension];
  if (!raw || typeof raw !== 'string') return ['__none__'];
  return [raw.toLowerCase()];
}

/**
 * Verifica se a voz casa com um conjunto de filtros ativos.
 * filters: { [dimension]: Set<string> }  — valores em lower-case
 *
 * Regra: dentro de uma dimensão é OR; entre dimensões é AND. Filtros vazios
 * (Set sem itens) são ignorados.
 *
 * @param {Object} voice
 * @param {Object} filters
 * @returns {boolean}
 */
export function matchesFilters(voice, filters) {
  if (!filters) return true;
  for (const dim of FACET_DIMENSIONS) {
    const wanted = filters[dim];
    if (!wanted || (wanted.size === 0)) continue;
    const values = getValuesForDimension(voice, dim);
    const hit = values.some((v) => wanted.has(v));
    if (!hit) return false;
  }
  return true;
}

/**
 * Conta quantas vozes existem por valor de uma dimensão, dado o conjunto
 * de filtros ativos das OUTRAS dimensões (faceted search à la Algolia).
 *
 * O filtro da própria dimensão é IGNORADO no cálculo, pra que as opções
 * dela continuem clicáveis mesmo após o usuário selecionar uma.
 *
 * @param {Array} voices       lista completa de vozes
 * @param {Object} activeFilters  { language: Set, accent: Set, ... }
 * @param {string} dimension      'language' | 'accent' | ... (de FACET_DIMENSIONS)
 * @returns {Object<string, number>}  { valor: count }
 */
export function facetCounts(voices, activeFilters, dimension) {
  if (!Array.isArray(voices) || !FACET_DIMENSIONS.includes(dimension)) return {};
  // Clone activeFilters MENOS a dimensão atual
  const filtersExceptCurrent = {};
  if (activeFilters && typeof activeFilters === 'object') {
    for (const key of Object.keys(activeFilters)) {
      if (key === dimension) continue;
      filtersExceptCurrent[key] = activeFilters[key];
    }
  }
  const counts = {};
  for (const v of voices) {
    if (!matchesFilters(v, filtersExceptCurrent)) continue;
    const values = getValuesForDimension(v, dimension);
    for (const val of values) {
      counts[val] = (counts[val] || 0) + 1;
    }
  }
  return counts;
}

/**
 * Exporta a lista canônica de dimensões pra uso em loops de UI.
 */
export const DIMENSIONS = [...FACET_DIMENSIONS];

// ═══════════════════════════════════════════════════════════════════════════
// THEME — tokens visuais pros dropdowns/popovers (em portal) da pasta voice/*
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Constante de cores pros 3 portais (LanguagePreviewDropdown, LanguageListPopover,
 * FilterPill). Usa CSS variables do design system (`--dp-*`) que adaptam
 * automaticamente entre tema dark/light via `html.light` override em
 * `index.css`. Border, hover e shadow usam o azul accent — mesmo padrão do
 * `ui/CustomDropdown.jsx` (referência do sistema).
 *
 * Vantagens vs hook `useTheme`:
 *   - Adapta com zero JS (puro CSS var, sem re-render)
 *   - Coerente visualmente com o resto do app (CustomDropdown, dialogs, etc)
 *   - Funções/components ficam mais simples (sem chamada de hook)
 */
export const VOICE_THEME = {
  // Background adapta via CSS var (dark #303030, light #FFFFFF)
  bg: 'var(--dp-bg-elevated)',
  // Border azul accent translúcida — igual ao CustomDropdown.
  // Consumers usam: `border: 1px solid ${VOICE_THEME.border}` ou similar.
  border: 'rgba(13, 174, 255, 0.15)',
  // Texto adapta via CSS vars
  text: 'var(--dp-text-primary)',
  textSecondary: 'var(--dp-text-secondary)',
  textTertiary: 'var(--dp-text-tertiary)',
  // Hover azul accent universal (mesmo dos itens do CustomDropdown)
  hover: 'rgba(13, 174, 255, 0.06)',
  hoverStrong: 'rgba(13, 174, 255, 0.10)',
  // Shadow padrão sistema
  shadow: '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(13, 174, 255, 0.05)',
  // Divider usa border-subtle (já adapta)
  divider: 'var(--dp-border-subtle)',
};

/**
 * Sentinela que representa "vozes sem voice_info" (CapCut/Multivoz/etc) nos
 * resultados de facetCounts. A UI pode renderizar como "Outros".
 */
export const FACET_NONE = '__none__';

// ═══════════════════════════════════════════════════════════════════════════
// SEARCH — busca inteligente (português + inglês, sem acento, multi-token)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Normaliza string pra comparação: lower-case + remove diacríticos.
 * Ex.: "Português" → "portugues", "Não-binário" → "nao-binario"
 */
function normalizeText(s) {
  if (s === null || s === undefined) return '';
  // NFD decompõe acentos (ex.: "á" → "a" + U+0301). Removemos os diacríticos
  // U+0300–U+036F pra busca case/acento-insensitive.
  return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Constrói o "haystack" de busca de uma voz combinando:
 *   - nome, slug
 *   - voice_info.voice_id (ID ElevenLabs)
 *   - voice_info.description
 *   - voice_info.use_case + tradução pt-BR
 *   - voice_info.descriptive + tradução
 *   - voice_info.category + tradução
 *   - voice_info.accent + tradução
 *   - voice_info.age + tradução
 *   - todos os idiomas (primário + verified_languages) + traduções
 * Tudo concatenado e normalizado.
 */
function buildSearchHaystack(voice) {
  const parts = [];
  if (voice.name) parts.push(voice.name);
  if (voice.slug) parts.push(voice.slug);
  const info = voice.voice_info;
  if (info && typeof info === 'object') {
    if (info.voice_id) parts.push(info.voice_id);
    if (info.description) parts.push(info.description);
    if (info.use_case) {
      parts.push(info.use_case);
      parts.push(translate(info.use_case, USE_CASE_PT));
    }
    if (info.descriptive) {
      parts.push(info.descriptive);
      parts.push(translate(info.descriptive, DESCRIPTIVE_PT));
    }
    if (info.category) {
      parts.push(info.category);
      parts.push(translate(info.category, CATEGORY_PT));
    }
    if (info.accent) {
      parts.push(info.accent);
      parts.push(translate(info.accent, ACCENT_PT));
    }
    if (info.age) {
      parts.push(info.age);
      parts.push(translate(info.age, AGE_PT));
    }
    // Idiomas: primário + verified_languages
    const langs = getLanguagesOfVoice(voice);
    for (const lang of langs) {
      parts.push(lang);
      parts.push(translate(lang, LANGUAGE_PT));
    }
  }
  return normalizeText(parts.join(' '));
}

/**
 * matchesSearch — verifica se a voz casa com a query.
 *
 * Comportamento:
 *   - Query vazia → true (tudo casa)
 *   - Normaliza query (lower-case + sem acentos)
 *   - Tokeniza por espaço; voz precisa conter TODOS os tokens (AND)
 *   - Comparação por substring no haystack montado em buildSearchHaystack
 *
 * @param {Object} voice
 * @param {string} query
 * @returns {boolean}
 */
export function matchesSearch(voice, query) {
  const q = normalizeText(query).trim();
  if (!q) return true;
  const haystack = buildSearchHaystack(voice);
  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((t) => haystack.includes(t));
}
